import React, { useState, useEffect, useMemo, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import {
  Calendar,
  Building2,
  User,
  Filter,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  CircleCheck,
  Ban,
  Loader2
} from 'lucide-react';
import { canCancelApprovedStayBooking } from '../../utils/stayBookingStatus';
import {
  ADMIN_ICON_STROKE,
  AdminPageHeader,
  AdminStatGrid,
  AdminToolbarIcon
} from '../../components/admin/AdminShared';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });
  const [approvingId, setApprovingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: pagination.limit,
        offset: pagination.offset
      };

      if (filters.status) {
        params.status = filters.status;
      }
      const trimmed = filters.search.trim();
      if (trimmed) {
        params.search = trimmed;
      }

      const response = await adminApi.getBookings(params);
      setBookings(response.bookings || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
        hasMore: response.pagination?.hasMore || false
      }));
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.search, pagination.limit, pagination.offset]);

  useEffect(() => {
    const delay = filters.search.trim() ? 320 : 0;
    const t = setTimeout(() => {
      fetchBookings();
    }, delay);
    return () => clearTimeout(t);
  }, [fetchBookings]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setFilters((prev) => ({ ...prev, search: searchTerm }));
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleApprove = async (bookingId) => {
    try {
      setApprovingId(bookingId);
      await adminApi.approveBooking(bookingId);
      await fetchBookings();
    } catch (error) {
      console.error('Error approving booking:', error);
      alert(error.response?.data?.error || 'Failed to approve booking');
    } finally {
      setApprovingId(null);
    }
  };

  const handleDecline = async (bookingId) => {
    const confirmed = window.confirm(
      'Decline this booking request?\n\nThis cannot be undone. The guest will see the booking as declined and reserved inventory will be released.'
    );
    if (!confirmed) return;

    try {
      setDecliningId(bookingId);
      await adminApi.declineBooking(bookingId);
      await fetchBookings();
    } catch (error) {
      console.error('Error declining booking:', error);
      alert(error.response?.data?.error || 'Failed to decline booking');
    } finally {
      setDecliningId(null);
    }
  };

  const handleCancelApproved = async (bookingId) => {
    const confirmed = window.confirm(
      'Cancel this approved booking?\n\nThis cannot be undone. The guest will see the booking as cancelled and reserved room nights will be returned to inventory.'
    );
    if (!confirmed) return;

    try {
      setCancellingId(bookingId);
      await adminApi.cancelApprovedBooking(bookingId);
      await fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert(error.response?.data?.error || 'Failed to cancel booking');
    } finally {
      setCancellingId(null);
    }
  };

  const getEffectiveStatus = (booking) => {
    const bookingStatus = booking.status?.toUpperCase() || '';
    const paymentStatus = booking.paymentStatus?.toUpperCase() || 'PENDING';

    if (bookingStatus === 'APPROVAL_PENDING' && paymentStatus === 'SUCCEEDED') {
      return 'approval_pending';
    }

    if (
      (bookingStatus === 'CONFIRMED' || bookingStatus === 'COMPLETED') &&
      paymentStatus === 'SUCCEEDED'
    ) {
      return bookingStatus === 'COMPLETED' ? 'completed' : 'confirmed';
    }

    if (paymentStatus === 'FAILED') {
      return 'pending';
    }

    if (bookingStatus === 'PENDING' || paymentStatus === 'PENDING') {
      return 'pending';
    }

    if (bookingStatus === 'DECLINED') {
      return 'declined';
    }

    if (bookingStatus === 'CANCELLED') {
      return 'cancelled';
    }

    if (bookingStatus === 'COMPLETED') {
      return 'completed';
    }

    return 'pending';
  };

  const canApproveBooking = (booking) =>
    getEffectiveStatus(booking) === 'approval_pending';

  const canDeclineBooking = canApproveBooking;

  const getStatusBadge = (booking) => {
    const effectiveStatus = getEffectiveStatus(booking);
    
    const statusConfig = {
      approval_pending: { color: 'bg-amber-100 text-amber-900', icon: Clock, label: 'Approval Pending' },
      declined: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Declined' },
      confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Confirmed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' },
      completed: { color: 'bg-[#FAF7F2] text-[#5c5c5c] border border-[#E5E3DF]', icon: CheckCircle, label: 'Completed' }
    };
    
    const config = statusConfig[effectiveStatus] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: 'Unknown' };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" strokeWidth={ADMIN_ICON_STROKE} />
        {config.label}
      </span>
    );
  };

  const bookingStatItems = useMemo(() => {
    const approvalPending = bookings.filter((b) => getEffectiveStatus(b) === 'approval_pending').length;
    const declined = bookings.filter((b) => getEffectiveStatus(b) === 'declined').length;
    const confirmed = bookings.filter((b) => getEffectiveStatus(b) === 'confirmed').length;
    const pending = bookings.filter((b) => getEffectiveStatus(b) === 'pending').length;
    const completed = bookings.filter((b) => getEffectiveStatus(b) === 'completed').length;
    return [
      { label: 'Listed (this page)', value: bookings.length, icon: Calendar },
      { label: 'Awaiting approval', value: approvalPending, icon: Clock },
      { label: 'Declined', value: declined, icon: XCircle },
      { label: 'Confirmed', value: confirmed, icon: CheckCircle },
      { label: 'Completed', value: completed, icon: CircleCheck },
      { label: 'Pending payment', value: pending, icon: Clock }
    ];
  }, [bookings]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  return (
    <div>
      <AdminPageHeader title="Bookings" subtitle="View and manage stay reservations">
        <span className="text-[13px] text-[#78736A] shrink-0 tabular-nums">
          Total in system: {pagination.total}
        </span>
      </AdminPageHeader>

      <AdminStatGrid items={bookingStatItems} loading={loading} />

        {/* Filters */}
        <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative flex items-center gap-2">
                <AdminToolbarIcon>
                  <Search className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} />
                </AdminToolbarIcon>
                <input
                  type="text"
                  placeholder="Search by booking ID, stay, customer name, or email..."
                  value={filters.search}
                  onChange={handleSearch}
                  className="w-full min-w-0 pl-4 pr-4 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AdminToolbarIcon>
                <Filter className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} />
              </AdminToolbarIcon>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="min-w-[10rem] px-4 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
              >
                <option value="">All Statuses</option>
                <option value="approval_pending">Approval Pending</option>
                <option value="declined">Declined</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[#FF5E4C] border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading bookings...</p>
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-[#78736A] mx-auto mb-4" strokeWidth={ADMIN_ICON_STROKE} />
              <p className="text-gray-600">No bookings found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#FAF7F2]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Booking ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stay
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Check In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Check Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Guests
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bookings.map((booking) => (
                    <tr key={booking.bookingId} className="hover:bg-[#FAF7F2]/60 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {booking.bookingId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-[#78736A] shrink-0" strokeWidth={ADMIN_ICON_STROKE} />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {booking.stay?.title || 'N/A'}
                            </div>
                            {booking.stay?.city && (
                              <div className="text-xs text-gray-500">
                                {booking.stay.city}, {booking.stay.state}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-[#78736A] shrink-0" strokeWidth={ADMIN_ICON_STROKE} />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {booking.user?.name || 'N/A'}
                            </div>
                            {booking.user?.email && (
                              <div className="text-xs text-gray-500">
                                {booking.user.email}
                              </div>
                            )}
                            {booking.user?.mobile && (
                              <div className="text-xs text-gray-500">
                                {booking.user.mobile}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(booking.checkIn)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(booking.checkOut)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {booking.guestCount || booking.numberOfGuests || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(booking.pricingSummary?.grandTotal || booking.totalAmount || booking.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(booking)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(booking.createdAt || booking.bookingDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {canApproveBooking(booking) ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleDecline(booking.bookingId)}
                              disabled={
                                decliningId === booking.bookingId ||
                                approvingId === booking.bookingId
                              }
                              className="inline-flex items-center px-3 py-1.5 rounded-full bg-white text-red-600 border border-red-200 text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                            >
                              {decliningId === booking.bookingId ? 'Declining…' : 'Decline'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleApprove(booking.bookingId)}
                              disabled={
                                approvingId === booking.bookingId ||
                                decliningId === booking.bookingId
                              }
                              className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#10B981] text-white text-xs font-medium hover:bg-[#059669] disabled:opacity-50"
                            >
                              {approvingId === booking.bookingId ? 'Approving…' : 'Approve'}
                            </button>
                          </div>
                        ) : canCancelApprovedStayBooking(booking) ? (
                          <button
                            type="button"
                            onClick={() => handleCancelApproved(booking.bookingId)}
                            disabled={
                              cancellingId === booking.bookingId ||
                              approvingId === booking.bookingId ||
                              decliningId === booking.bookingId
                            }
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-[#78736A] border border-[#E5E3DF] text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                          >
                            {cancellingId === booking.bookingId ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={ADMIN_ICON_STROKE} />
                            ) : (
                              <Ban className="w-3.5 h-3.5" strokeWidth={ADMIN_ICON_STROKE} />
                            )}
                            {cancellingId === booking.bookingId ? 'Cancelling…' : 'Cancel'}
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && bookings.length > 0 && (
            <div className="bg-[#FAF7F2] px-6 py-4 border-t border-[#E5E3DF] flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} bookings
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                  disabled={pagination.offset === 0}
                  className="px-4 py-2 border border-[#E5E3DF] rounded-full text-sm font-medium text-black hover:bg-white bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                  disabled={!pagination.hasMore}
                  className="px-4 py-2 border border-[#E5E3DF] rounded-full text-sm font-medium text-black hover:bg-white bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default AdminBookings;

